import React from "react";
import { Card, Checkbox, Divider, List, Space, Tag, Typography } from "antd";
import type { CheckboxChangeEvent } from "antd/es/checkbox";
import type { GlobalToken } from "antd/es/theme/interface";
import type { ScreenMap } from "antd/es/_util/responsiveObserver";
import { getOriginColor } from "./constants";
import { RuleRow } from "./types";
import { useTranslation } from "react-i18next";

const { Title, Text, Paragraph } = Typography;

type RulesSectionProps = {
    origins: string[];
    checkedOrigins: string[];
    groupedRules: Record<string, RuleRow[]>;
    hasActiveFilter: boolean;
    onToggleOrigin: (origin: string, checked: boolean) => void;
    token: GlobalToken;
    screens: ScreenMap;
};

const RulesSection: React.FC<RulesSectionProps> = ({
    origins,
    checkedOrigins,
    groupedRules,
    hasActiveFilter,
    onToggleOrigin,
    screens,
    token,
}) => {
    const handleCheckboxChange = (origin: string) => (event: CheckboxChangeEvent) => {
        onToggleOrigin(origin, event.target.checked);
    };
    const { t } = useTranslation();

    return (
        <Card
            title={
                <Title level={2} style={{ color: token.colorPrimary, marginBottom: 0 }}>
                    {t("games.details.beerpong.rules.heading")}
                </Title>
            }
        >
            <Paragraph>
                {t("games.details.beerpong.rules.intro")}
            </Paragraph>
            <Card className="beerpong-rules-filter-card" style={{ margin: "18px 0", background: token.colorFillAlter }} variant="borderless">
                <Text strong>{t("games.details.beerpong.rules.visibleSets")}</Text>
                <Space
                    direction={screens.xs ? "vertical" : "horizontal"}
                    wrap
                    style={{ marginLeft: 12, gap: 12 }}
                >
                    {origins.map((origin) => (
                        <Checkbox
                            key={origin}
                            checked={checkedOrigins.includes(origin)}
                            onChange={handleCheckboxChange(origin)}
                            style={{ minWidth: 120, minHeight: screens.xs ? 44 : undefined, display: "inline-flex", alignItems: "center" }}
                        >
                            <Tag color={getOriginColor(origin)}>{origin}</Tag>
                        </Checkbox>
                    ))}
                </Space>
            </Card>
            {origins
                .filter((origin) => checkedOrigins.includes(origin))
                .map((origin) => (
                    <div key={origin} style={{ marginBottom: screens.xs ? 24 : 32 }}>
                        <Divider orientation="left" style={{ fontSize: screens.xs ? 16 : 18 }}>
                            <Tag color={getOriginColor(origin)} style={{ fontSize: 14, padding: "4px 12px" }}>
                                {origin}
                            </Tag>
                        </Divider>
                        <List<RuleRow>
                            dataSource={groupedRules[origin] || []}
                            renderItem={(item) => (
                                <List.Item
                                    style={{
                                        display: "block",
                                        padding: screens.xs ? "10px 0" : "16px 0",
                                        border: "none",
                                        borderBottom: `1px solid ${token.colorBorderSecondary}`,
                                        background: token.colorBgElevated,
                                    }}
                                >
                                    <Text strong style={{ marginLeft: 8, fontSize: screens.xs ? 15 : 16 }}>
                                        {item.rulename}
                                    </Text>
                                    <br />
                                    <div style={{ marginLeft: 12 }}>
                                        <Text>{item.rule}</Text>
                                    </div>
                                </List.Item>
                            )}
                            locale={{
                                emptyText: <Text type="secondary">{t("games.details.beerpong.rules.empty")}</Text>,
                            }}
                            style={{ background: "none" }}
                        />
                    </div>
                ))}
            {!hasActiveFilter && <Text type="secondary">{t("games.details.beerpong.rules.noMatches")}</Text>}
        </Card>
    );
};

export default RulesSection;
